package com.htmlagent.site.components.sections

import androidx.compose.runtime.Composable
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.theme.GateGuestVariant
import com.htmlagent.site.theme.GateWarningStyle
import com.htmlagent.site.theme.Icons
import com.htmlagent.site.theme.LinkBackStyle
import com.htmlagent.site.theme.LinkDoneIconStyle
import com.htmlagent.site.theme.LinkDoneStyle
import com.htmlagent.site.theme.LinkEmailStyle
import com.htmlagent.site.theme.LinkHeaderStyle
import com.htmlagent.site.theme.LinkLeadStyle
import com.htmlagent.site.theme.LinkStepStyle
import com.htmlagent.site.theme.LinkTitleStyle
import com.htmlagent.site.theme.MatchChoiceVariant
import com.htmlagent.site.theme.MatchChoicesStyle
import com.htmlagent.site.theme.MatchHintStyle
import com.htmlagent.site.theme.MatchNumberStyle
import com.htmlagent.site.theme.MatchPanelStyle
import com.htmlagent.site.theme.Tokens
import com.htmlagent.site.theme.WaitingDotStyle
import com.htmlagent.site.theme.WaitingStatusStyle
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Button as DomButton
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * The window that asked for a link, waiting for it: where it went, and, in a panel of its own,
 * the number to pick if it opens in another browser. It signs in on its own; there is nothing to
 * press but the way back. Only the waiting line is a live region, so the rest is read once.
 */
@Composable
fun WaitingForLink(sentTo: String, match: Int, onOtherEmail: () -> Unit) {
    val t = LocalStrings.current
    Div(LinkStepStyle.toModifier().toAttrs()) {
        Div(LinkHeaderStyle.toModifier().toAttrs()) {
            H2(LinkTitleStyle.toModifier().toAttrs()) { Text(t.gate.linkSentTitle) }
            P(LinkLeadStyle.toModifier().toAttrs()) {
                Text(t.gate.linkSentLead + " ")
                Span(LinkEmailStyle.toModifier().toAttrs()) { Text(sentTo) }
                Text(". " + t.gate.linkSentExpiry)
            }
        }

        Div(MatchPanelStyle.toModifier().toAttrs()) {
            P(MatchHintStyle.toModifier().toAttrs()) { Text(t.gate.matchLead) }
            Span(MatchNumberStyle.toModifier().toAttrs()) { Text(match.toString()) }
            Span(WaitingStatusStyle.toModifier().toAttrs { attr("role", "status") }) {
                Span(WaitingDotStyle.toModifier().toAttrs { attr("aria-hidden", "true") })
                Text(t.gate.waiting)
            }
        }

        DomButton(
            attrs = LinkBackStyle.toModifier().toAttrs {
                attr("type", "button")
                onClick { onOtherEmail() }
            },
        ) {
            Text(t.gate.otherEmail)
        }
    }
}

/**
 * The link opened in a browser other than the one that asked: pick the number that one shows,
 * and it is that one that signs in. Somebody who asked for nothing sees no number anywhere, so the
 * warning, set apart under the choices, tells them what to do instead.
 */
@Composable
fun ApproveLink(email: String, choices: List<Int>, onPick: (Int) -> Unit, onCancel: () -> Unit) {
    val t = LocalStrings.current
    Div(LinkStepStyle.toModifier().toAttrs()) {
        Div(LinkHeaderStyle.toModifier().toAttrs()) {
            H2(LinkTitleStyle.toModifier().toAttrs()) { Text(t.gate.approveTitle) }
            P(LinkLeadStyle.toModifier().toAttrs()) {
                Text(t.gate.approveLead + " ")
                Span(LinkEmailStyle.toModifier().toAttrs()) { Text(email) }
                Text(".")
            }
        }

        Div(MatchChoicesStyle.toModifier().toAttrs()) {
            choices.forEach { number ->
                Button(
                    onClick = { onPick(number) },
                    modifier = Modifier.attrsModifier {
                        attr("type", "button")
                        attr("aria-label", t.gate.approveChoice(number))
                    },
                    variant = MatchChoiceVariant,
                ) {
                    Text(number.toString())
                }
            }
        }

        P(GateWarningStyle.toModifier().toAttrs()) { Text(t.gate.approveWarning) }

        SecondaryButton(t.gate.approveCancel, onCancel)
    }
}

/** Approved: the other window is in, and this one has nothing left to do. */
@Composable
fun LinkApproved() {
    val t = LocalStrings.current
    Div(LinkDoneStyle.toModifier().toAttrs()) {
        Span(LinkDoneIconStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) {
            Glyph(Icons.Check, Tokens.iconLg.value())
        }
        H2(LinkTitleStyle.toModifier().toAttrs { attr("role", "status") }) { Text(t.gate.approvedTitle) }
        P(LinkLeadStyle.toModifier().toAttrs()) { Text(t.gate.approvedBody) }
    }
}

@Composable
private fun SecondaryButton(label: String, onClick: () -> Unit) {
    Button(
        onClick = { onClick() },
        modifier = Modifier.attrsModifier { attr("type", "button") },
        variant = GateGuestVariant,
    ) {
        Text(label)
    }
}
